import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { KpiCard, StagePill, SectionHeader } from "@/components/common/Widgets";
import { fmtDate } from "@/lib/format";
import { Users, FileText, CheckCircle2, XCircle, Clock, FileCheck, ShieldCheck, History } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip as RTooltip } from "recharts";

export default function DashboardPage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [kpis, setKpis] = useState(null);
  const [pipeline, setPipeline] = useState([]);
  const [queue, setQueue] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [k, p, q, a] = await Promise.all([
          api.get("/dashboard/kpis"),
          api.get("/dashboard/pipeline"),
          api.get("/dashboard/my-queue"),
          api.get("/dashboard/recent-audit?limit=12"),
        ]);
        setKpis(k.data); setPipeline(p.data); setQueue(q.data); setAudit(a.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl heading font-semibold">{t("dashboard.welcome")}, {user?.name?.split(" - ")[0]}</h1>
          <p className="text-sm text-muted-foreground mt-1">Pipeline health, your queue, and the latest activity.</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/applications/new"><Button data-testid="dashboard-new-application-button">New Application</Button></Link>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <KpiCard icon={FileText} label={t("dashboard.totalApplications")} value={kpis?.total_applications ?? "—"} hint={`${t("dashboard.customers")}: ${kpis?.customers ?? 0}`} testid="kpi-total-apps" />
        <KpiCard icon={Clock} label={t("dashboard.inProgress")} value={kpis?.in_progress ?? "—"} hint="Across all stages" testid="kpi-in-progress" />
        <KpiCard icon={FileCheck} label={t("dashboard.sanctioned")} value={kpis?.sanctioned ?? "—"} hint="This period" testid="kpi-sanctioned" accent="accent" />
        <KpiCard icon={XCircle} label={t("dashboard.rejected")} value={kpis?.rejected ?? "—"} hint="This period" testid="kpi-rejected" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-4 lg:col-span-2 shadow-card">
          <SectionHeader title="Pipeline by Stage" subtitle="Application count per workflow stage" />
          <div className="h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={pipeline.map(p => ({ stage: p.stage.replace(/_/g, ' '), count: p.count }))} margin={{ left: 10, right: 10, top: 10, bottom: 60 }}>
                <XAxis dataKey="stage" angle={-30} textAnchor="end" interval={0} fontSize={11} />
                <YAxis allowDecimals={false} fontSize={11} />
                <RTooltip />
                <Bar dataKey="count" fill="hsl(186 55% 28%)" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-4 shadow-card">
          <SectionHeader title="Quick Stats" subtitle="" />
          <div className="space-y-2.5">
            <Row label="Pending KYC" value={kpis?.pending_kyc} icon={Users} />
            <Row label="In Credit Review" value={kpis?.in_credit} icon={FileText} />
            <Row label="Awaiting Approval" value={kpis?.in_approval} icon={CheckCircle2} />
            <Row label="Total Customers" value={kpis?.customers} icon={Users} />
          </div>
          <div className="mt-4 pt-3 border-t border-border">
            <Link to="/customers/new"><Button variant="outline" className="w-full" data-testid="dashboard-new-customer-button">Onboard New Customer</Button></Link>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-4 lg:col-span-2 shadow-card">
          <SectionHeader title="My Queue" subtitle="Applications assigned to your stage" action={<Link to="/applications"><Button variant="ghost" size="sm">View all</Button></Link>} />
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground uppercase tracking-wide bg-muted/40">
                <tr>
                  <th className="text-left px-3 py-2">Ref</th>
                  <th className="text-left px-3 py-2">Customer</th>
                  <th className="text-left px-3 py-2">Type</th>
                  <th className="text-right px-3 py-2">CC (₹L)</th>
                  <th className="text-right px-3 py-2">TL (₹L)</th>
                  <th className="text-left px-3 py-2">Stage</th>
                  <th className="text-left px-3 py-2">Updated</th>
                </tr>
              </thead>
              <tbody>
                {queue.length === 0 && (
                  <tr><td colSpan={7} className="text-center py-8 text-muted-foreground text-sm">No applications in your queue yet. <Link to="/applications/new" className="text-accent underline">Create one</Link>.</td></tr>
                )}
                {queue.map((a) => (
                  <tr key={a.id} className="border-t border-border hover:bg-muted/40">
                    <td className="px-3 py-2 mono text-xs">{a.ref_no}</td>
                    <td className="px-3 py-2"><Link to={`/applications/${a.id}`} className="hover:text-accent">{a.customer_name}</Link></td>
                    <td className="px-3 py-2 text-xs">{a.loan_type}</td>
                    <td className="px-3 py-2 num-right">{a.cc_proposed ?? '—'}</td>
                    <td className="px-3 py-2 num-right">{a.tl_proposed ?? '—'}</td>
                    <td className="px-3 py-2"><StagePill stage={a.stage} /></td>
                    <td className="px-3 py-2 text-xs text-muted-foreground mono">{fmtDate(a.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Card className="p-4 shadow-card">
          <SectionHeader title="Recent Audit Events" subtitle="Latest 12 actions across system" />
          <ul className="space-y-3">
            {audit.length === 0 && <li className="text-sm text-muted-foreground">No events yet.</li>}
            {audit.map((e) => (
              <li key={e.id} className="text-sm flex items-start gap-2.5">
                <div className="mt-0.5 h-2 w-2 rounded-full bg-accent" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="font-medium">{e.actor_name}</span>
                    <span className="text-xs text-muted-foreground">({e.actor_role})</span>
                    <span className="text-xs text-accent mono">{e.action}</span>
                  </div>
                  <div className="text-[11px] text-muted-foreground mono">{fmtDate(e.timestamp)}</div>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </div>
  );
}

function Row({ label, value, icon: Icon }) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
        <span className="text-sm">{label}</span>
      </div>
      <span className="mono font-semibold tabular">{value ?? "—"}</span>
    </div>
  );
}
